<template>
  <div :id="`question_${index + 1}`" class="py-4 flex relative">
    <span class="w-20 py-5 px-2 text-3xl leading-relaxed absolute transform -translate-y-3 lg:px-6 lg:translate-y-0">{{ index + 1 }}</span>
    <div class="min-w-0 pl-14 py-3 flex-1 lg:pl-20 lg:py-5">
      <div class="-ml-20 pl-20 flex items-center gap-x-2">
        <div class="flex-1 relative">
          <p class="w-full rounded-lg py-2 px-4 bg-gray-100 font-bold">{{ question__user.questionTitle }}</p>
          <div v-if="question.correctAnswerCnt > 1" class="absolute -top-5">
            <p class="text-xs text-red-400">{{ $t("exam.question.score", { '0' : question.correctAnswerCnt }) }}</p>
          </div>
        </div>
        <div v-if="bankExam.examType === 'QUIZ'" class="flex items-center gap-x-0.5">
          <GrmTextField type="number" v-model="question__user.questionScore" class="w-20" disabled />
          <span class="text-xs">{{ $t("exam.question.point") }}</span>
        </div>
      </div>
      <div class="mt-2 flex flex-col gap-y-1 relative">
        <div v-for="(answer, i) in question__user.answerList"
             :key="i"
             class="gap-x-1"
        >
          <div class="min-w-0 max-w-max flex-1 rounded flex items-baseline gap-x-2">
            <div class="relative">
              <button class="w-5 h-5 border rounded-full flex justify-center items-center text-sm select-none transition"
                      :class="[
                        answer.correct && showAnswer ? 'border-blue-400 bg-blue-400 text-white' : 'border-gray-800',
                        { 'pointer-events-none': hasEmceeRole() }
                      ]"
                      @click="onSelectAnswer(answer)"
              >{{ i + 1 }}</button>
              <span v-if="false" class="w-5 h-5 bg-exam-check-red block absolute -top-2.5 -left-0.5 pointer-events-none" />
            </div>
            <div class="flex flex-col gap-y-1">
              <p class="min-w-0 max-w-max flex-1 rounded-lg py-2 px-3 bg-gray-100 text-xs">{{ answer.answerTitle }}</p>
              <div v-if="hasEmceeRole()"
                   class="flex flex-wrap gap-2"
              >
                <div v-for="(submitUser, i) in submitUserList(answer)" :key="i" class="flex items-center flex-wrap gap-2">
                  <p class="w-max rounded px-2 bg-gray-200 text-xs text-gray-800 font-bold leading-6">{{ submitUser || $t("exam.unknownUser") }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {cloneDeep} from "lodash";

import GrmTextField from "@/components/Common/textField/GrmTextField.vue";

export default {
  name: 'RoomExamQuestionChoice',
  components: {
    GrmTextField
  },
  props: {
    index: { type: Number, default: 0 },
    question: { type: Object, default: () => ({}), required: true },
  },
  data() {
    return {
      question__user: {},
      userAnswerList: [],
    }
  },
  computed: {
    ...mapState('exam', [
      'bankExam',
      'showAnswer',
      'roomExamUsersAnswerObj',
    ]),
    ...mapGetters('user', [
      'me',
      'hasEmceeRole',
    ]),

    userAnswerCount() {
      return this.question__user.answerList.filter(answer => answer.correct).length;
    },
    submitUserList() {
      return answer => {
        const submitUserList = [];
        const hasUserQuestionList = Object.values(this.roomExamUsersAnswerObj).find(roomExamUsersAnswer => roomExamUsersAnswer.userQuestionList && roomExamUsersAnswer.username);
        if (!hasUserQuestionList) {
          return submitUserList;
        }

        for (const { userQuestionList, username } of Object.values(this.roomExamUsersAnswerObj)) {
          if (!userQuestionList) {
            continue;
          }

          for (const userQuestion of userQuestionList) {
            if (userQuestion.questionId === this.question.questionId && userQuestion.userAnswerList?.find(userAnswer => userAnswer.answerId === answer.answerId)) {
              submitUserList.push(username);
            }
          }
        }
        return submitUserList;
      }
    },
  },
  created() {
    this.setQuestionUser();
  },
  methods: {
    ...mapActions('exam', [
      'submitUserAnswers',
    ]),

    setQuestionUser() {
      this.question__user = { ...cloneDeep(this.question) };

      this.question__user.answerList = this.question__user.answerList.map(answer => {
        return this.hasEmceeRole() ? answer : {
          ...answer,
          correct: false,
        }
      });

      if (this.roomExamUsersAnswerObj[this.me.userId]) {
        const { userQuestionList } = this.roomExamUsersAnswerObj[this.me.userId];
        for (const userQuestion of userQuestionList) {
          if (!userQuestion.userAnswerList) {
            continue;
          }

          for (const userAnswer of userQuestion.userAnswerList) {
            const answer = this.question__user.answerList.find(answer => answer.answerId === userAnswer.answerId);
            if (answer) {
              answer.correct = true;
            }
          }
        }
      }
    },
    onSelectAnswer(answer) {
      if (this.hasEmceeRole()) {
        return;
      }

      const block = this.bankExam.examType === 'QUIZ' && !answer.correct && this.userAnswerCount === this.question.correctAnswerCnt;
      if (block) {
        this.$alert(this.$t('exam.error.exceed', { '0' : this.question.correctAnswerCnt }));
        return;
      }

      try {
        this.onSetUserAnswerList(answer);
        this.submitUserAnswers({
          userQuestion: {
            questionId: this.question.questionId,
            questionType: this.question.questionType,
            questionScore: this.question.questionScore,
            questionOrder: this.question.questionOrder,
            userAnswerList: this.userAnswerList,
          }
        });

        answer.correct = !answer.correct;
        this.question__user.correctAnswerCnt = this.question__user.answerList.filter(answer => answer.correct).length;
      } catch (e) {
        this.$alert(this.$t('exam.error.retry'));
      }
    },
    onSetUserAnswerList(answer) {
      this.question__user.answerList = this.question__user.answerList
          .map(userAnswer => {
            if (userAnswer.answerId === answer.answerId) {
              return {
                ...userAnswer,
                correct: !userAnswer.correct,
              }
            }
            return userAnswer;
          })

      this.userAnswerList = this.question__user.answerList.filter(userAnswer => userAnswer.correct);
    }
  }
}
</script>